import React, { useEffect, useMemo, useRef, useState } from "react";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { getSupabaseClient } from "../lib/supabase";
import BackgroundCheckCard from "../components/BackgroundCheckCard";

type Nullable<T> = T | null | undefined;

type Identity = {
  name: string;
  email: string | null;
  avatarUrl: string | null;
};

type FormState = {
  fullName: string;
  displayName: string;
  phone: string;
  location: string;
  bio: string;
  about: string;
  imageUrl: string;
  yearsPracticing: string;
  ceremoniesCount: string;
  modality: string;
  employmentType: string;
  personalRelationship: string;
  worksWithOtherFacilitators: "yes" | "no" | "";
  hasMedicalDirector: "yes" | "no" | "";
  isMainIncome: "yes" | "no" | "";
  genderId: string;
  experienceTypes: string[];
  legalStatus: string;
  supportTypes: string[];
  travelWillingness: string[];
  serviceLocations: string[];
  focusAreas: string[];
  excludedAreas: string[];
  worksWithMedications: string;
  medicationQualifications: string;
  specialGroups: string[];
  religiousBeliefs: string[];
};

const DEFAULT_FORM: FormState = {
  fullName: "",
  displayName: "",
  phone: "",
  location: "",
  bio: "",
  about: "",
  imageUrl: "",
  yearsPracticing: "",
  ceremoniesCount: "",
  modality: "",
  employmentType: "",
  personalRelationship: "",
  worksWithOtherFacilitators: "",
  hasMedicalDirector: "",
  isMainIncome: "",
  genderId: "",
  experienceTypes: [],
  legalStatus: "",
  supportTypes: [],
  travelWillingness: [],
  serviceLocations: [],
  focusAreas: [],
  excludedAreas: [],
  worksWithMedications: "",
  medicationQualifications: "",
  specialGroups: [],
  religiousBeliefs: [],
};

type StatusBanner = {
  type: "success" | "error";
  message: string;
} | null;

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_FOCUS_AREAS = 10;

const EXPERIENCE_TYPE_OPTIONS = [
  "1:1 Journeys or Sessions — Private, personalized experiences with dedicated support",
  "Small Group Experiences — Shared healing spaces with multiple participants",
  "Retreat Center Facilitation — Multi-day immersions with structured guidance",
  "Online Journey Support / Sitting — Virtual presence and harm-reduction support",
  "Microdosing Coaching or Support — Gentle, sub-perceptual dosing with guidance",
  "Medication Tapering Support — In collaboration with licensed providers",
  "Preparation & Integration Only — No in-person dosing, focused on readiness and processing",
  "Family / Couples Containers — Facilitated journeys for relational healing",
  "Other",
];

const LEGAL_STATUS_OPTIONS = [
  "Government regulated options only — I work only in legal psychedelic modalities where I live",
  "Underground practitioner — I work in contexts that may not be formally legal where I live",
  "Both — I work in legal contexts and in underground settings",
  "Prefer not to say",
];

const SUPPORT_TYPE_OPTIONS = [
  "Microdose Support — Gentle, sub-perceptual dosing with guidance, structure, and harm reduction",
  "Macrodose Support — Full, immersive psychedelic experiences with trained facilitation",
  "Preparation Support — Help getting ready for a journey — intentions, mindset, safety planning",
  "Integration Support — Support after an experience to process insights and apply them",
  "Other",
];

const TRAVEL_WILLINGNESS_OPTIONS = [
  "Local / Regional — I'm available for seekers in my local area or region",
  "Within State/Province — I can travel within my state or province",
  "Nationally — I can travel to other states or provinces",
  "Internationally — I can travel abroad for the right clients",
  "Seekers Travel to Me — People typically travel to my location",
  "Virtual / Online — I work remotely via video",
];

const SERVICE_LOCATION_OPTIONS = [
  "At My Home — Sessions at my personal residence",
  "Office or Professional Space — In a dedicated professional setting",
  "At the Client's Home — In-home sessions at the seeker's location",
  "Online / Virtual — Remote support via video",
  "At Retreat Centers — At dedicated retreat facilities",
  "Church, Non-Profit, or Spiritual Community — Community-based settings",
  "Other",
];

const SPECIAL_GROUP_OPTIONS = [
  "BIPOC",
  "LGBTQ+",
  "Women",
  "Men",
  "Couples",
  "Families",
  "Veterans",
  "Seniors",
  "Complex PTSD",
  "Other",
  "None",
];

const RELIGIOUS_BELIEF_OPTIONS = [
  "Agnostic",
  "Atheist",
  "New Age / Spiritual",
  "Christian (Non-Denominational)",
  "Catholic",
  "Mormon / Latter-day Saint",
  "Jewish",
  "Muslim",
  "Buddhist",
  "Hindu",
  "Other",
  "None",
];

const MEDICATION_OPTIONS = ["Yes", "No"];

const GENDER_OPTIONS = [
  "Male",
  "Female",
  "Transgender",
  "Non-binary",
  "Other",
  "Prefer not to say",
];

const YEARS_PRACTICING_OPTIONS = [
  "Less than 2 years",
  "2–5 years",
  "5+ years",
  "10+ years",
];

const CEREMONIES_COUNT_OPTIONS = [
  "Less than 5",
  "5–15",
  "15–50",
  "50–100",
  "100–200",
  "200+",
];

const EMPLOYMENT_TYPE_OPTIONS = [
  "Full time",
  "Part time",
];

const FOCUS_AREA_GROUPS: Record<string, string[]> = {
  "Personal Growth & Exploration": [
    "Personal Development",
    "Empowerment",
    "Creative Expression",
    "Spiritual Exploration",
    "Perspective Shifting",
  ],
  "Mental & Emotional Wellbeing": [
    "Anxiety",
    "Depression",
    "Mood Disorders",
    "ADD / ADHD",
    "OCD",
    "Autism",
    "Bipolar",
    "Eating Disorders",
    "General Mental Health Support",
  ],
  "Trauma & Recovery": [
    "PTSD",
    "Early Childhood Trauma",
    "Sexual Trauma",
    "Relationship Trauma",
    "Religious Trauma",
    "Racial Trauma",
    "Physical Trauma",
    "Cult / High-Control Group Recovery",
    "Shame",
    "Inner Child Work",
  ],
  "Addiction & Behavioral Patterns": [
    "Addiction / Substance Use",
    "Anger Management",
    "Pain Management",
  ],
  "Relationships & Family": [
    "Couples Support",
    "Divorce & Separation",
    "Healthy Relationship Skills",
    "Interpersonal Relationships",
    "Parenting / Children",
  ],
  "Life Transitions & Identity": [
    "Birth & Postpartum Support",
    "Career Support",
    "Life Transitions",
    "Grief & Loss",
    "Gender Identity",
    "End-of-Life Support",
  ],
  "Other": ["Other"],
};

function normaliseYesNo(value: Nullable<string>): "yes" | "no" | "" {
  if (!value) return "";
  const normalised = String(value).trim().toLowerCase();
  if (["yes", "y", "true", "1"].includes(normalised)) return "yes";
  if (["no", "n", "false", "0"].includes(normalised)) return "no";
  return "";
}

function presentYesNo(value: "yes" | "no" | ""): string | null {
  if (value === "") return null;
  return value;
}

function formatZip(value: string): string {
  const digits = value.replace(/[^0-9]/g, "");
  if (digits.length <= 5) return digits;
  return digits.slice(0, 5);
}

// Format phone digits as (XXX) XXX-XXXX for US numbers
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}

export default function ProfilePage(): JSX.Element {
  const supabase = getSupabaseClient();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [initialForm, setInitialForm] = useState<FormState>(DEFAULT_FORM);
  const [identity, setIdentity] = useState<Identity>({ name: "", email: null, avatarUrl: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<StatusBanner>(null);
  const [locationPreview, setLocationPreview] = useState<string | null>(null);
  const [locationStatus, setLocationStatus] = useState<"idle" | "loading" | "error" | "success">("idle");
  const [professionalId, setProfessionalId] = useState<string | null>(null);
  const [focusAreaError, setFocusAreaError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [zipValidationError, setZipValidationError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [experienceId, setExperienceId] = useState<string | null>(null);
  const locationCacheRef = useRef<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const localPreviewUrlRef = useRef<string | null>(null);

  const allFocusOptions = useMemo(
    () => Array.from(new Set(Object.values(FOCUS_AREA_GROUPS).flat())),
    []
  );

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function fetchProfile() {
      try {
        setLoading(true);
        setStatus(null);

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) {
          console.error("Auth error fetching guide profile", authError);
          if (active) {
            setStatus({ type: "error", message: "Unable to load profile – please refresh." });
            setLoading(false);
          }
          return;
        }

        if (!user) {
          if (active) {
            setStatus({ type: "error", message: "You must be logged in to view your profile." });
            setLoading(false);
          }
          return;
        }

        const userId = user.id;

        const [profileRes, basicInfoRes, enhancedRes, professionalRes, experienceRes] = await Promise.all([
          supabase.from("profiles").select("full_name, phone").eq("id", userId).maybeSingle(),
          supabase.from("basic_info").select("location, bio").eq("user_id", userId).maybeSingle(),
          supabase.from("guides_enhanced_profile").select("about, image_url, display_name").eq("guide_id", userId).maybeSingle(),
          supabase
            .from("guide_professional")
            .select(
              "id, years_practicing, ceremonies_count, modality, employment_type, personal_relationship, works_with_other_facilitators, has_medical_director, is_main_income, gender_id"
            )
            .eq("guide_id", userId)
            .maybeSingle(),
          supabase
            .from("guide_professional_experience")
            .select(
              "id, experience_types, legal_status, support_types, travel_willingness, service_locations, focus_areas, excluded_areas, works_with_medications, medication_qualifications, special_groups, religious_beliefs"
            )
            .eq("guide_id", userId)
            .maybeSingle(),
        ]);

        if (!active) return;

        const profile = profileRes.data ?? null;
        const basicInfo = basicInfoRes.data ?? null;
        const enhancedProfile = enhancedRes.data ?? null;
        const professional = professionalRes.data ?? null;
        const experience = experienceRes.data ?? null;

        setProfessionalId(professional?.id ?? null);
        setExperienceId(experience?.id ?? null);

        const merged: FormState = {
          fullName: profile?.full_name ?? "",
          displayName: enhancedProfile?.display_name ?? "",
          phone: formatPhone((profile?.phone ?? "").replace(/^\+1/, "")),
          location: basicInfo?.location ?? "",
          bio: basicInfo?.bio ?? "",
          about: enhancedProfile?.about ?? "",
          imageUrl: enhancedProfile?.image_url ?? "",
          yearsPracticing: professional?.years_practicing ?? "",
          ceremoniesCount: professional?.ceremonies_count ?? "",
          modality: professional?.modality ?? "",
          employmentType: professional?.employment_type ?? "",
          personalRelationship: professional?.personal_relationship ?? "",
          worksWithOtherFacilitators: normaliseYesNo(professional?.works_with_other_facilitators),
          hasMedicalDirector: normaliseYesNo(professional?.has_medical_director),
          isMainIncome: normaliseYesNo(professional?.is_main_income),
          genderId: professional?.gender_id ?? "",
          experienceTypes: experience?.experience_types ?? [],
          legalStatus: experience?.legal_status ?? "",
          supportTypes: experience?.support_types ?? [],
          travelWillingness: experience?.travel_willingness ?? [],
          serviceLocations: experience?.service_locations ?? [],
          focusAreas: experience?.focus_areas ?? [],
          excludedAreas: experience?.excluded_areas ?? [],
          worksWithMedications: experience?.works_with_medications ?? "",
          medicationQualifications: experience?.medication_qualifications ?? "",
          specialGroups: experience?.special_groups ?? [],
          religiousBeliefs: experience?.religious_beliefs ?? [],
        };

        const derivedName =
          merged.fullName || user.user_metadata?.full_name || user.user_metadata?.name || user.email || "";

        setForm(merged);
        setInitialForm({ ...merged });
        setAvatarPreview(merged.imageUrl || null);
        setIdentity({
          name: derivedName,
          email: user.email ?? null,
          avatarUrl: merged.imageUrl || null,
        });

        const formattedZip = formatZip(merged.location);
        setLocationPreview(formattedZip ? formattedZip : null);
        setLocationStatus("idle");
      } catch (error) {
        console.error("Failed to load guide profile", error);
        if (active) {
          setStatus({ type: "error", message: "Failed to load your profile. Please try again." });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void fetchProfile();

    return () => {
      active = false;
    };
  }, [supabase]);

  useEffect(() => {
    const zip = formatZip(form.location);

    if (!zip || zip.length !== 5) {
      setLocationPreview(zip || null);
      setLocationStatus("idle");
      return;
    }

    if (locationCacheRef.current.has(zip)) {
      setLocationPreview(locationCacheRef.current.get(zip) ?? zip);
      setLocationStatus("success");
      return;
    }

    let active = true;
    const controller = new AbortController();
    setLocationStatus("loading");

    const fetchLocation = async () => {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zip}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          if (active) {
            setLocationPreview(`${zip}`);
            setLocationStatus("error");
          }
          return;
        }

        const data: any = await response.json();
        const place = Array.isArray(data?.places) && data.places.length > 0 ? data.places[0] : null;
        const city: string | undefined = place?.["place name"] ?? place?.place;
        const state: string | undefined = place?.["state abbreviation"] ?? place?.state;
        const label = city && state ? `${city}, ${state} (${zip})` : zip;

        locationCacheRef.current.set(zip, label);
        if (active) {
          setLocationPreview(label);
          setLocationStatus("success");
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn("Zip lookup failed", error);
        if (active) {
          setLocationPreview(zip);
          setLocationStatus("error");
        }
      }
    };

    void fetchLocation();

    return () => {
      active = false;
      controller.abort();
    };
  }, [form.location]);

  const handleInputChange = (field: keyof FormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = event.target.value;
    let formatted = value;
    if (field === "location") {
      formatted = formatZip(value);
      if (zipValidationError) setZipValidationError(null);
    } else if (field === "phone") {
      formatted = formatPhone(value);
      if (phoneError) setPhoneError(null);
    }
    setForm((prev) => ({
      ...prev,
      [field]: formatted,
    }));
  };

  const toggleSimpleArrayField = (
    field:
      | "experienceTypes"
      | "supportTypes"
      | "travelWillingness"
      | "serviceLocations"
      | "specialGroups"
      | "religiousBeliefs",
    option: string
  ) => {
    setForm((prev) => {
      const current = prev[field];
      const exists = current.includes(option);
      const next = exists ? current.filter((value) => value !== option) : [...current, option];
      return {
        ...prev,
        [field]: next,
      };
    });
  };

  const handleFocusAreaToggle = (area: string) => {
    setForm((prev) => {
      if (prev.focusAreas.includes(area)) {
        setFocusAreaError(null);
        return {
          ...prev,
          focusAreas: prev.focusAreas.filter((value) => value !== area),
        };
      }
      if (prev.focusAreas.length >= MAX_FOCUS_AREAS) {
        setFocusAreaError(`You can select up to ${MAX_FOCUS_AREAS} focus areas. Remove one to add another.`);
        return prev;
      }
      setFocusAreaError(null);
      return {
        ...prev,
        focusAreas: [...prev.focusAreas, area],
        excludedAreas: prev.excludedAreas.filter((value) => value !== area),
      };
    });
  };

  const handleExcludedAreaToggle = (area: string) => {
    setForm((prev) => {
      if (prev.excludedAreas.includes(area)) {
        return {
          ...prev,
          excludedAreas: prev.excludedAreas.filter((value) => value !== area),
        };
      }
      setFocusAreaError(null);
      return {
        ...prev,
        excludedAreas: [...prev.excludedAreas, area],
        focusAreas: prev.focusAreas.filter((value) => value !== area),
      };
    });
  };

  const headerDisplayName = form.fullName || identity.name || "Your profile";

  const pickAvatarFile = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      event.target.value = "";
      return;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setAvatarError("Please choose a JPG, PNG, or WEBP image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError("Image must be 5MB or smaller.");
      event.target.value = "";
      return;
    }

    setAvatarError(null);

    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
      localPreviewUrlRef.current = null;
    }

    const previewUrl = URL.createObjectURL(file);
    localPreviewUrlRef.current = previewUrl;

    setAvatarFile(file);
    setAvatarPreview(previewUrl);
    setForm((prev) => ({
      ...prev,
      imageUrl: previewUrl,
    }));
    event.target.value = "";
  };

  const handleRemoveAvatar = () => {
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
      localPreviewUrlRef.current = null;
    }
    setAvatarFile(null);
    setAvatarPreview(null);
    setForm((prev) => ({
      ...prev,
      imageUrl: "",
    }));
  };

  const handleReset = () => {
    setForm({ ...initialForm });
    setStatus(null);
    setFocusAreaError(null);
    setAvatarError(null);
    setAvatarFile(null);
    setAvatarPreview(initialForm.imageUrl || null);
    setIdentity((prev) => ({
      ...prev,
      avatarUrl: initialForm.imageUrl || null,
    }));
  };

  const handleSave = async () => {
    // Validate phone if provided
    if (form.phone) {
      const phoneDigits = form.phone.replace(/\D/g, "");
      if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
        setPhoneError("Please enter a valid 10-digit phone number");
        return;
      }
    }

    // Validate ZIP if provided
    if (form.location) {
      const zipDigits = form.location.replace(/\D/g, "");
      if (zipDigits.length > 0 && zipDigits.length !== 5) {
        setZipValidationError("Please enter a valid 5-digit ZIP code");
        return;
      }
    }

    try {
      setSaving(true);
      setStatus(null);
      setAvatarError(null);

      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        throw authError;
      }

      if (!user) {
        throw new Error("Not authenticated");
      }

      const userId = user.id;
      const env = (window as any).__ENV__ ?? null;
      const formData: FormState = { ...form };

      let imageUrlToPersist: string | null = null;

      if (avatarFile) {
        if (!env?.SUPABASE_URL) {
          throw new Error("Environment configuration missing. Please refresh and try again.");
        }

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!session) {
          throw new Error("Not authenticated");
        }

        const base64Data = await fileToDataUrl(avatarFile);

        const response = await fetch(`${env.SUPABASE_URL}/functions/v1/enhanced-profile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            about: formData.about,
            image: base64Data,
            mimeType: avatarFile.type,
            skipDb: true,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          console.error("Avatar upload via edge function failed", result);
          throw new Error(result?.error ?? "Unable to upload your profile photo. Please try again.");
        }

        imageUrlToPersist = result?.imageUrl ?? null;
        formData.imageUrl = imageUrlToPersist ?? "";
      } else if (!formData.imageUrl) {
        imageUrlToPersist = null;
      } else if (formData.imageUrl.startsWith("blob:")) {
        // Blob URLs only exist if a new file was selected without avatarFile set (shouldn't happen)
        imageUrlToPersist = null;
        formData.imageUrl = "";
      } else {
        imageUrlToPersist = formData.imageUrl;
      }

      // Sanitize phone to E.164 format (+1XXXXXXXXXX) before saving
      const phoneDigits = (formData.phone || "").replace(/\D/g, "");
      const sanitizedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : (phoneDigits ? `+1${phoneDigits}` : null);

      const profileUpdate = {
        full_name: formData.fullName || null,
        phone: sanitizedPhone,
      } as Record<string, string | null>;

      const profileResult = await supabase.from("profiles").update(profileUpdate).eq("id", userId);

      if (profileResult.error) {
        throw profileResult.error;
      }

      // Look up city, state, and timezone from the zip code
      let zipCity: string | null = null;
      let zipState: string | null = null;
      let zipTimezone: string | null = null;
      const zipDigits = (formData.location || "").replace(/\D/g, "");
      if (zipDigits.length === 5) {
        const { data: zipRows } = await supabase
          .from("postal_code_timezones")
          .select("city, state, timezone_id")
          .eq("country", "US")
          .eq("postal_code", zipDigits)
          .limit(1);
        if (zipRows && zipRows.length > 0) {
          zipCity = zipRows[0].city;
          zipState = zipRows[0].state;
          zipTimezone = zipRows[0].timezone_id;
        }
      }

      const basicResult = await supabase
        .from("basic_info")
        .upsert(
          {
            user_id: userId,
            location: formData.location || null,
            bio: formData.bio || null,
            city: zipCity,
            state: zipState,
            timezone: zipTimezone,
          },
          { onConflict: "user_id" }
        );

      if (basicResult.error) {
        throw basicResult.error;
      }

      const enhancedResult = await supabase
        .from("guides_enhanced_profile")
        .upsert(
          {
            guide_id: userId,
            about: formData.about || null,
            display_name: formData.displayName || null,
            image_url: imageUrlToPersist,
          },
          { onConflict: "guide_id" }
        );

      if (enhancedResult.error) {
        throw enhancedResult.error;
      }

      const professionalPayload = {
        years_practicing: formData.yearsPracticing || null,
        ceremonies_count: formData.ceremoniesCount || null,
        modality: formData.modality || null,
        employment_type: formData.employmentType || null,
        personal_relationship: formData.personalRelationship || null,
        works_with_other_facilitators: presentYesNo(formData.worksWithOtherFacilitators),
        has_medical_director: presentYesNo(formData.hasMedicalDirector),
        is_main_income: presentYesNo(formData.isMainIncome),
        gender_id: formData.genderId || null,
      } as Record<string, string | null>;

      if (professionalId) {
        const professionalResult = await supabase
          .from("guide_professional")
          .update(professionalPayload)
          .eq("id", professionalId);

        if (professionalResult.error) {
          throw professionalResult.error;
        }
      } else {
        const professionalInsert = await supabase
          .from("guide_professional")
          .insert({
            guide_id: userId,
            ...professionalPayload,
          })
          .select("id")
          .single();

        if (professionalInsert.error) {
          throw professionalInsert.error;
        }

        setProfessionalId(professionalInsert.data?.id ?? null);
      }

      const experiencePayload = {
        guide_id: userId,
        experience_types: formData.experienceTypes,
        legal_status: formData.legalStatus || null,
        support_types: formData.supportTypes,
        travel_willingness: formData.travelWillingness,
        service_locations: formData.serviceLocations,
        focus_areas: formData.focusAreas,
        excluded_areas: formData.excludedAreas,
        works_with_medications: formData.worksWithMedications || null,
        medication_qualifications: formData.worksWithMedications === "Yes" ? (formData.medicationQualifications || null) : null,
        special_groups: formData.specialGroups,
        religious_beliefs: formData.religiousBeliefs,
      };

      if (experienceId) {
        const experienceResult = await supabase
          .from("guide_professional_experience")
          .update(experiencePayload)
          .eq("id", experienceId);

        if (experienceResult.error) {
          throw experienceResult.error;
        }
      } else {
        const experienceInsert = await supabase
          .from("guide_professional_experience")
          .insert(experiencePayload)
          .select("id")
          .single();

        if (experienceInsert.error) {
          throw experienceInsert.error;
        }

        setExperienceId(experienceInsert.data?.id ?? null);
      }

      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      setAvatarFile(null);
      setAvatarPreview(imageUrlToPersist || null);

      setInitialForm({ ...formData });
      setForm({ ...formData });
      setStatus({ type: "success", message: "Profile updated successfully." });
      setIdentity((prev) => ({
        name: formData.fullName || prev.name,
        email: prev.email,
        avatarUrl: imageUrlToPersist || null,
      }));
    } catch (error) {
      console.error("Error saving profile", error);
      setStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to save your profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(form) !== JSON.stringify(initialForm);

  const renderLoadingSkeleton = () => (
    <div className="animate-pulse space-y-4">
      <div className="h-4 rounded bg-[#E8DFBD]" />
      <div className="h-4 rounded bg-[#E8DFBD]" />
      <div className="h-4 rounded bg-[#E8DFBD]" />
    </div>
  );

  const renderTogglePill = ({
    label,
    active,
    onClick,
    role,
  }: {
    label: string;
    active: boolean;
    onClick: () => void;
    role: "checkbox" | "radio";
  }) => (
    <button
      key={label}
      type="button"
      role={role}
      aria-pressed={active}
      aria-checked={active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#2C2731]/40 ${
        active
          ? "border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] shadow-sm"
          : "border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731] hover:bg-[#F3EBCD]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="rounded-2xl border border-[#2C2731] bg-[#2C2731] p-6 text-[#FFFCEF] shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="relative h-20 w-20 overflow-hidden rounded-full border border-[#FFFCEF]/30 bg-[#FFFCEF]/10">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Profile avatar" className="h-full w-full object-cover" />
              ) : identity.avatarUrl ? (
                <img src={identity.avatarUrl} alt="Profile avatar" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[#FFFCEF]/80">
                  <PhotoIcon className="h-10 w-10" />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#FFFCEF]/70">Guide profile</p>
              <h1 className="mt-1 text-2xl font-semibold">{headerDisplayName}</h1>
              {identity.email && <p className="text-sm text-[#FFFCEF]/80">{identity.email}</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            <button
              type="button"
              onClick={pickAvatarFile}
              className="inline-flex items-center justify-center rounded-md border border-[#FFFCEF]/40 bg-[#FFFCEF]/10 px-4 py-2 text-sm font-medium text-[#FFFCEF] transition hover:bg-[#FFFCEF]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF]/60"
            >
              Upload new photo
            </button>
            {form.imageUrl && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                className="text-xs text-[#FFFCEF]/80 underline underline-offset-2 transition hover:text-[#FFFCEF]"
              >
                Remove current photo
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.webp"
          onChange={handleAvatarChange}
        />
        {avatarError && <p className="mt-4 rounded-md border border-red-300 bg-red-100 p-3 text-sm text-red-800">{avatarError}</p>}
      </div>

      {status && (
        <div
          className={`rounded-lg border p-4 text-sm ${
            status.type === "success"
              ? "border-[#2C2731] bg-[#E8DFBD] text-[#2C2731]"
              : "border-red-300 bg-red-100 text-red-800"
          }`}
        >
          {status.message}
        </div>
      )}

      <div className="space-y-6">
        <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-medium text-[#2C2731]">Basic details</h2>
              <p className="text-sm text-[#2C2731]/70">
                Your contact information helps seekers know how to reach you and where you primarily work.
              </p>
            </div>
            <span className="text-xs uppercase tracking-wide text-[#2C2731]/60">Required</span>
          </div>

          {loading ? (
            renderLoadingSkeleton()
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-[#2C2731]">Full name</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={handleInputChange("fullName")}
                  className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Add your full name"
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-[#2C2731]">
                  Display name <span className="font-normal text-[#2C2731]/60">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={handleInputChange("displayName")}
                  maxLength={100}
                  className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Name shown to seekers (leave blank to use full name)"
                />
                <p className="mt-1 text-xs text-[#2C2731]/70">
                  This name will be shown to seekers instead of your legal name.
                </p>
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-[#2C2731]">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handleInputChange("phone")}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                    phoneError
                      ? "border-red-400 focus:border-red-500 focus:ring-red-400"
                      : "border-[#2C2731] focus:border-[#2C2731] focus:ring-[#2C2731]"
                  }`}
                  placeholder="(555) 123-4567"
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                )}
              </div>

              <div className="md:col-span-1">
                <label className="mb-1 block text-sm font-medium text-[#2C2731]">Primary location (ZIP)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.location}
                  onChange={handleInputChange("location")}
                  maxLength={5}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                    zipValidationError
                      ? "border-red-400 focus:border-red-500 focus:ring-red-400"
                      : "border-[#2C2731] focus:border-[#2C2731] focus:ring-[#2C2731]"
                  }`}
                  placeholder="ZIP code"
                />
                {zipValidationError && (
                  <p className="mt-1 text-xs text-red-600">{zipValidationError}</p>
                )}
                {!zipValidationError && form.location && (
                  <p className="mt-1 text-xs text-[#2C2731]/70">
                    {locationStatus === "loading" && "Looking up city…"}
                    {locationStatus === "success" && locationPreview}
                    {locationStatus === "error" && `${locationPreview ?? form.location} (could not verify)`}
                    {locationStatus === "idle" && locationPreview}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-[#2C2731]">Seeker visible bio</label>
                <textarea
                  value={form.about}
                  onChange={handleInputChange("about")}
                  rows={6}
                  maxLength={2000}
                  className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Share your facilitation approach, training, and what seekers can expect when working with you."
                />
                <div className="mt-1 flex justify-between">
                  <p className="text-xs text-[#2C2731]/70">This section appears on your public profile for seekers.</p>
                  <p className={`text-xs ${form.about.length >= 1900 ? "text-red-600" : "text-[#2C2731]/70"}`}>
                    {form.about.length}/2000
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-[#2C2731]">Professional snapshot</h2>
            <p className="text-sm text-[#2C2731]/70">
              Optional details that help communicate your experience level to seekers.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Years practicing</label>
              <select
                value={form.yearsPracticing}
                onChange={handleInputChange("yearsPracticing")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                {YEARS_PRACTICING_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Ceremonies facilitated</label>
              <select
                value={form.ceremoniesCount}
                onChange={handleInputChange("ceremoniesCount")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                {CEREMONIES_COUNT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/*<div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Primary modality</label>
              <input
                type="text"
                value={form.modality}
                onChange={handleInputChange("modality")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                placeholder="e.g. Psychedelic-assisted therapy"
              />
            </div>*/}

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Employment type</label>
              <select
                value={form.employmentType}
                onChange={handleInputChange("employmentType")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Provides majority income?</label>
              <select
                value={form.isMainIncome}
                onChange={handleInputChange("isMainIncome")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Has medical director?</label>
              <select
                value={form.hasMedicalDirector}
                onChange={handleInputChange("hasMedicalDirector")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Works with other facilitators?</label>
              <select
                value={form.worksWithOtherFacilitators}
                onChange={handleInputChange("worksWithOtherFacilitators")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Gender</label>
              <select
                value={form.genderId}
                onChange={handleInputChange("genderId")}
                className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              >
                <option value="">Select</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Personal relationship with psychedelics</label>
              <textarea
              value={form.personalRelationship}
              onChange={handleInputChange("personalRelationship")}
              rows={4}
              className="w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
              placeholder="e.g. 1:1 and small group facilitation"
              />
            </div>
        </section>

        <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-[#2C2731]">Practice & matching preferences</h2>
            <p className="text-sm text-[#2C2731]/70">
              These answers come from onboarding and drive which seekers we show you. Update them anytime to refine your
              matches.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Experience types</h3>
              <p className="text-xs text-[#2C2731]/70">Select all that apply.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {EXPERIENCE_TYPE_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.experienceTypes.includes(option),
                    onClick: () => toggleSimpleArrayField("experienceTypes", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Legal status</h3>
              <p className="text-xs text-[#2C2731]/70">How do you currently practice?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {LEGAL_STATUS_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.legalStatus === option,
                    onClick: () =>
                      setForm((prev) => ({
                        ...prev,
                        legalStatus: prev.legalStatus === option ? "" : option,
                      })),
                    role: "radio",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Support types</h3>
              <p className="text-xs text-[#2C2731]/70">Select the services you currently offer.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUPPORT_TYPE_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.supportTypes.includes(option),
                    onClick: () => toggleSimpleArrayField("supportTypes", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Travel willingness</h3>
              <p className="text-xs text-[#2C2731]/70">Where are you willing to meet seekers?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {TRAVEL_WILLINGNESS_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.travelWillingness.includes(option),
                    onClick: () => toggleSimpleArrayField("travelWillingness", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Service locations</h3>
              <p className="text-xs text-[#2C2731]/70">Select all settings where you hold space.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {SERVICE_LOCATION_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.serviceLocations.includes(option),
                    onClick: () => toggleSimpleArrayField("serviceLocations", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-medium text-[#2C2731]">
                    Focus areas ({form.focusAreas.length}/{MAX_FOCUS_AREAS})
                  </h3>
                  <p className="text-xs text-[#2C2731]/70">Choose up to {MAX_FOCUS_AREAS} areas you love to support.</p>
                </div>
                {focusAreaError && <p className="text-xs font-medium text-red-600">{focusAreaError}</p>}
              </div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                {Object.entries(FOCUS_AREA_GROUPS).map(([group, options]) => (
                  <div key={group} className="rounded-lg border border-[#2C2731] bg-[#E8DFBD] p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-[#2C2731]">{group}</h4>
                      <span className="text-xs text-[#2C2731]/60">{options.length} options</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {options.map((option) =>
                        renderTogglePill({
                          label: option,
                          active: form.focusAreas.includes(option),
                          onClick: () => handleFocusAreaToggle(option),
                          role: "checkbox",
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Areas to avoid matching</h3>
              <p className="text-xs text-[#2C2731]/70">
                We will avoid matching you with seekers focused on these topics.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {allFocusOptions.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.excludedAreas.includes(option),
                    onClick: () => handleExcludedAreaToggle(option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Do you work with seekers on medications?</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {MEDICATION_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.worksWithMedications === option,
                    onClick: () =>
                      setForm((prev) => ({
                        ...prev,
                        worksWithMedications: prev.worksWithMedications === option ? "" : option,
                      })),
                    role: "radio",
                  })
                )}
              </div>
            </div>

            {form.worksWithMedications === "Yes" && (
              <div>
                <h3 className="text-sm font-medium text-[#2C2731]">Medication qualifications</h3>
                <p className="text-xs text-[#2C2731]/70">
                  Describe your qualifications and safety protocols for working with clients on psychiatric medications.
                </p>
                <textarea
                  value={form.medicationQualifications}
                  onChange={handleInputChange("medicationQualifications")}
                  rows={4}
                  className="mt-3 w-full rounded-lg border border-[#2C2731] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Describe any formal training, clinical background, use of medical professionals for screening, safety protocols, or experience with medication-interaction harm reduction..."
                />
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Special groups</h3>
              <p className="text-xs text-[#2C2731]/70">Select any groups you have particular experience with.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {SPECIAL_GROUP_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.specialGroups.includes(option),
                    onClick: () => toggleSimpleArrayField("specialGroups", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-[#2C2731]">Spiritual or religious alignment</h3>
              <p className="text-xs text-[#2C2731]/70">Optional, but helps seekers feel more comfortable.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {RELIGIOUS_BELIEF_OPTIONS.map((option) =>
                  renderTogglePill({
                    label: option,
                    active: form.religiousBeliefs.includes(option),
                    onClick: () => toggleSimpleArrayField("religiousBeliefs", option),
                    role: "checkbox",
                  })
                )}
              </div>
            </div>
          </div>
        </section>
        {/* Background Check */}
        <BackgroundCheckCard />
      </div>

      <div className="mt-8 flex flex-col gap-3 border-t border-[#2C2731] pt-6 md:flex-row md:justify-end">
        <button
          type="button"
          disabled={saving || !hasChanges}
          onClick={handleReset}
          className={`inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
            !hasChanges || saving
              ? "cursor-not-allowed border-[#2C2731] bg-[#E8DFBD] text-[#2C2731]/60"
              : "border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]"
          }`}
        >
          Reset changes
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`inline-flex items-center justify-center rounded-md px-5 py-2 text-sm font-medium text-[#FFFCEF] transition-colors ${
            saving || !hasChanges
              ? "cursor-not-allowed bg-[#2C2731]/40"
              : "bg-[#2C2731] hover:bg-[#241F2A]"
          }`}
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  </div>
);
}
